import { Alert, Collapse, Descriptions, Tag, Typography } from "antd";

import type { CdrFlag } from "./clinicalReview.types";
import { flagDetailLabel, formatFlagDetailValue } from "./fieldLabel";

type Props = {
  flags: CdrFlag[];
};

/** Why a record was flagged for review.
 *
 * Deliberately offers no way to clear or suppress a flag: suppressing a
 * finding without a documented rationale is not permitted, so the way to
 * dispose of one is to disposition the record it sits on.
 */
export default function ClinicalReviewFlags({ flags }: Props) {
  if (flags.length === 0) return null;

  return (
    <div className="mb-4" data-testid="cdr-flags">
      <Typography.Text strong>
        Flagged for review ({flags.length})
      </Typography.Text>

      <Collapse
        className="mt-2"
        size="small"
        defaultActiveKey={flags.length === 1 ? [String(flags[0].id)] : []}
        items={flags.map((flag) => ({
          key: String(flag.id),
          label: (
            <div className="flex flex-col gap-1">
              <span>{flag.headline}</span>
              <span className="flex flex-wrap items-center gap-1">
                <Tag color="gold">{flag.predicate_label}</Tag>
                {flag.stale && (
                  <Tag color="default">Record changed since flagged</Tag>
                )}
              </span>
            </div>
          ),
          children: (
            <div className="flex flex-col gap-3">
              <Alert
                type="info"
                showIcon
                message={flag.question}
                description={flag.rationale}
              />

              <Descriptions size="small" column={1} bordered>
                {Object.entries(flag.detail).map(([key, value]) => (
                  <Descriptions.Item key={key} label={flagDetailLabel(key)}>
                    {formatFlagDetailValue(value)}
                  </Descriptions.Item>
                ))}
              </Descriptions>

              <Typography.Text type="secondary" className="text-xs">
                {flag.predicate_label} v{flag.predicate_version} · thresholds
                from {flag.threshold_source}
                {flag.policy_version !== null
                  ? ` (policy v${flag.policy_version})`
                  : ""}
              </Typography.Text>
            </div>
          ),
        }))}
      />
    </div>
  );
}
